import React from "react";
import { Dialog } from "@headlessui/react";
import InlineCheckbox from "../../InlineCheckbox";
import TextEditor from "../../TextEditor";

export default function PatientRecordsModalContent({
    patient,
    handlePatient,
    handleSubmitPatient,
    loadingSubmitRecords,
    onClose,
}) {
    return (
        <div className="flex max-h-[78vh] flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                <div className="mt-3 text-left sm:mt-5">
                    <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                    >
                        Antecedentes del paciente
                    </Dialog.Title>
                    <div className="flex-col space-y-8 p-2 sm:space-y-4 sm:p-6 sm:py-5">
                        <div>
                            <label className="font-semibold text-gray-700 dark:text-gray-200">
                                Antecedente personal
                            </label>
                        </div>
                        <div>
                            <div className="grid grid-cols-2 gap-y-4 px-4 sm:grid-cols-3">
                                <InlineCheckbox value={patient?.dm} handleChange={handlePatient} name="dm" label="DM" />
                                <InlineCheckbox value={patient?.hta} handleChange={handlePatient} name="hta" label="HTA" />
                                <InlineCheckbox value={patient?.gl} handleChange={handlePatient} name="gl" label="Glaucoma" />
                            </div>
                            <TextEditor
                                name="othersPersonal"
                                label=""
                                value={patient?.othersPersonal}
                                handleChange={handlePatient}
                                error={null}
                                required={false}
                                containerClassName="mt-4 px-4 pb-10"
                                height={160}
                            />
                        </div>
                        <div>
                            <label className="font-semibold text-gray-700 dark:text-gray-200">
                                Antecedente ocular
                            </label>
                        </div>
                        <div>
                            <div className="grid grid-cols-2 gap-y-4 px-4 sm:grid-cols-3">
                                <InlineCheckbox
                                    value={patient?.strabismus}
                                    handleChange={handlePatient}
                                    name="strabismus"
                                    label="Estrabismo"
                                />
                                <InlineCheckbox
                                    value={patient?.glaucoma}
                                    handleChange={handlePatient}
                                    name="glaucoma"
                                    label="Glaucoma"
                                />
                            </div>
                            <TextEditor
                                name="othersOcular"
                                label=""
                                value={patient?.othersOcular}
                                handleChange={handlePatient}
                                error={null}
                                required={false}
                                containerClassName="mt-4 px-4 pb-10"
                                height={160}
                            />
                        </div>
                        <div>
                            <label className="font-semibold text-gray-700 dark:text-gray-200">
                                Cirugias oftalmologicas
                            </label>
                        </div>
                        <div>
                            <div className="grid grid-cols-2 gap-y-4 px-4 sm:grid-cols-3">
                                <InlineCheckbox
                                    value={patient?.cataractSurgery}
                                    handleChange={handlePatient}
                                    name="cataractSurgery"
                                    label="Cataratas"
                                />
                                <InlineCheckbox
                                    value={patient?.glaucomaSurgery}
                                    handleChange={handlePatient}
                                    name="glaucomaSurgery"
                                    label="Glaucoma"
                                />
                                <InlineCheckbox
                                    value={patient?.refractiveSurgery}
                                    handleChange={handlePatient}
                                    name="refractiveSurgery"
                                    label="Refractiva"
                                />
                            </div>
                            <TextEditor
                                name="othersSurgery"
                                label=""
                                value={patient?.othersSurgery}
                                handleChange={handlePatient}
                                error={null}
                                required={false}
                                containerClassName="mt-4 px-4 pb-10"
                                height={160}
                            />
                        </div>
                        <div className="mt-10">
                            <label className="font-semibold text-gray-700 dark:text-gray-200">
                                Antecedentes familiares
                            </label>
                        </div>
                        <div className="space-y-3">
                            <div className="grid grid-cols-4 w-full font-medium text-gray-700 dark:text-gray-200">
                                <label className="invisible">Referencia</label>
                                <label>Diabetes</label>
                                <label>HTA</label>
                                <label>Glaucoma</label>
                            </div>
                            {[
                                ["Padre", "fatherDm", "fatherHta", "fatherGlaucoma"],
                                ["Madre", "motherDm", "motherHta", "motherGlaucoma"],
                                ["Hermanos", "siblingsDm", "siblingsHta", "siblingsGlaucoma"],
                                ["Otros", "otherDm", "otherHta", "otherGlaucoma"],
                            ].map(([label, dmName, htaName, glaucomaName]) => (
                                <div
                                    key={label}
                                    className="grid grid-cols-4 w-full font-medium text-gray-700 dark:text-gray-200"
                                >
                                    <label className="text-sm font-medium">{label}</label>
                                    <InlineCheckbox value={patient?.[dmName]} handleChange={handlePatient} name={dmName} />
                                    <InlineCheckbox value={patient?.[htaName]} handleChange={handlePatient} name={htaName} />
                                    <InlineCheckbox
                                        value={patient?.[glaucomaName]}
                                        handleChange={handlePatient}
                                        name={glaucomaName}
                                        className="-mx-5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                </div>
                            ))}
                            <TextEditor
                                name="othersFamily"
                                label=""
                                value={patient?.othersFamily}
                                handleChange={handlePatient}
                                error={null}
                                required={false}
                                containerClassName="mt-4 px-4 pb-10"
                                height={160}
                            />
                        </div>
                    </div>
                    <div className="w-full px-4 pt-6">
                        <div>
                            <label className="font-semibold text-gray-700 dark:text-gray-200">
                                Medicamentos
                            </label>
                        </div>
                        <TextEditor
                            name="medications"
                            label=""
                            value={patient?.medications}
                            handleChange={handlePatient}
                            error={null}
                            required={false}
                            containerClassName="px-4 pb-10"
                            height={160}
                        />
                    </div>
                </div>
            </div>
            <div className="mt-4 border-t border-slate-200 bg-white pt-4 dark:border-slate-700 dark:bg-gray-900">
                <div className="grid grid-cols-1 gap-3 sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={handleSubmitPatient}
                >
                    {loadingSubmitRecords ? (
                        <div className="mx-5 h-5 w-5 animate-spin rounded-full border-b-2 border-white" />
                    ) : (
                        "Guardar"
                    )}
                </button>
                <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:col-start-1 sm:text-sm"
                    onClick={onClose}
                >
                    Cancelar
                </button>
                </div>
            </div>
        </div>
    );
}
